import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import { useUsers } from "modules/providers/users-provider";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { EmailLockedIcon, MailPersonIcon } from "modules/icons";
import { QuillMention } from "modules/quill/mentions/use-quill-mentions";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import { getUserFullName } from "modules/utils/Functions";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import { MENTION_TYPE } from "modules/utils/Constants";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { MessageThreadFragment } from "gql/graphql";
import AddEmails from "./add-emails";

interface IProps {
  draftId: string;
  appendMention: (mention: QuillMention) => void;
  messageThread?: MessageThreadFragment;
}

const ComposeBottomToolbarExternalUserButton = ({
  draftId,
  appendMention,
  messageThread,
}: IProps) => {
  const { draft, isDraftLoaded, bottomButtonTypes } = useMessageThreadDraft({
    draftId,
  });

  const { usersById } = useUsers();

  const isLocked = messageThread?.isLocked ?? draft?.isLocked ?? false;

  const lockedUser = useMemo(
    () =>
      isLocked && messageThread?.lockedUserId
        ? usersById[messageThread.lockedUserId]
        : null,
    [isLocked, messageThread?.lockedUserId, usersById],
  );

  const { onOpen, onClose, isOpen } = useDisclosure();

  const onAddEmails = useCallback(
    (userIds: string[]) => {
      userIds.forEach((userId) => {
        const user = usersById[userId];
        if (user) {
          appendMention({
            id: user.id,
            value: user.email ?? "",
            type: MENTION_TYPE.USER,
            isExternal: true,
          });
        }
      });
    },
    [appendMention, usersById],
  );

  if (!draft || !isDraftLoaded) return null;

  if (!bottomButtonTypes.includes("external-user")) {
    return null;
  }

  if (isLocked) {
    return (
      <SyncoTooltip
        label={`This conversation was set to “Private” by ${getUserFullName(
          lockedUser,
        )} to prevent anyone in the thread from adding users and groups to it.. Only the person who makes it Private can remove the setting.`}
      >
        <EmailLockedIcon fill={colors.gray40} boxSize={5} mx={2} />
      </SyncoTooltip>
    );
  }

  return (
    <Popover
      placement="top"
      onOpen={onOpen}
      onClose={onClose}
      isOpen={isOpen}
      isLazy
    >
      <PopoverTrigger>
        <CircularIconButton
          aria-label="Add External Parties"
          title="Add External Parties"
          size="xs"
          variant="ghost"
          color={colors.gray40}
          icon={<MailPersonIcon boxSize={5} fill={colors.gray40} />}
          onClick={onOpen}
          tabIndex={-1}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent borderRadius={10} w="500px">
          <PopoverArrow />
          <AddEmails onClose={onClose} callback={onAddEmails} />
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default memo(ComposeBottomToolbarExternalUserButton);
